<script setup lang="ts">
import type { Subscription } from '@entities/subscription'

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { OrderRelationLink } from '@shared/ui/links'
import { AppText } from '@shared/ui/typography'
import { AppTd, AppTr } from '@shared/ui/tables'
import { BtnIcon } from '@shared/ui/buttons'

import { useSubscriptionRow } from '../model/subscription-row.hook'
import SubscriptionDealOwner from './SubscriptionDealOwner.vue'
import SubscriptionProviderCell from './SubscriptionProviderCell.vue'
import SubscriptionRelationsCell from './SubscriptionRelationsCell.vue'
import SubscriptionRowActions from './SubscriptionRowActions.vue'
import SubscriptionStatusCell from './SubscriptionStatusCell.vue'

defineOptions({
  name: 'SubscriptionRow',
})

const props = withDefaults(defineProps<{
  subscription: Subscription
  canManage: boolean
  /** Off on the customer's own Subscriptions tab, where every row is that customer. */
  withCustomer?: boolean
}>(), {
  withCustomer: true,
})

const {
  isFailed,
  isExpanded,
  isProcessing,
  customerName,
  customerUrl,
  kidLinks,
  disciplineLinks,
  autoRenewLabel,
  autoRenewColor,
  subscriptionName,
  paymentMethod,
  nextInvoice,
  emptyCell,
  openDetails,
  toggleExpanded,
  changeDealOwner,
} = useSubscriptionRow({
  subscription: computed(() => props.subscription),
})
</script>

<template>
  <AppTr :class="{ 'bg-red': isFailed }">
    <AppTd
      v-if="!withCustomer"
      class="zero-padding subscription-row__icon-cell"
    >
      <SubscriptionRowActions
        :subscription="subscription"
        :can-manage="canManage"
      />
    </AppTd>
    <AppTd class="medium">
      <button
        type="button"
        class="subscription-row__id"
        :title="`Details of subscription #${subscription.id}`"
        @click="openDetails"
      >
        <AppText
          name="body1"
          color="blue-600"
        >
          {{ subscription.id }}
        </AppText>
      </button>
    </AppTd>
    <AppTd
      v-if="withCustomer"
      class="medium"
    >
      <OrderRelationLink
        v-if="customerUrl && customerName"
        :href="customerUrl"
        :text="customerName"
        :notes="subscription.customer?.notes"
      />
      <AppText
        v-else
        name="body1"
        color="slate-900"
      >
        {{ emptyCell }}
      </AppText>
    </AppTd>
    <AppTd class="medium">
      <SubscriptionRelationsCell
        :links="kidLinks"
        :empty-text="emptyCell"
      />
    </AppTd>
    <AppTd class="medium">
      <SubscriptionRelationsCell
        :links="disciplineLinks"
        :empty-text="emptyCell"
      />
    </AppTd>
    <AppTd class="medium">
      <AppText
        name="body1"
        :color="autoRenewColor"
      >
        {{ autoRenewLabel }}
      </AppText>
    </AppTd>
    <AppTd class="medium subscription-row__name">
      <AppText
        name="body1"
        color="slate-900"
      >
        {{ subscriptionName }}
      </AppText>
    </AppTd>
    <AppTd class="medium subscription-row__nowrap">
      <SubscriptionProviderCell :subscription="subscription" />
    </AppTd>
    <AppTd class="medium">
      <AppText
        name="body1"
        color="slate-900"
      >
        {{ paymentMethod }}
      </AppText>
    </AppTd>
    <AppTd class="medium">
      <SubscriptionStatusCell :subscription="subscription" />
    </AppTd>
    <AppTd class="medium subscription-row__owner">
      <SubscriptionDealOwner
        :subscription="subscription"
        :agent-id="subscription.agent_id"
        :disabled="!canManage || isProcessing"
        @update:agent-id="changeDealOwner"
      />
    </AppTd>
    <AppTd class="medium subscription-row__nowrap">
      <AppText
        name="body1"
        color="slate-900"
      >
        {{ nextInvoice }}
      </AppText>
    </AppTd>
    <AppTd class="zero-padding subscription-row__icon-cell">
      <BtnIcon
        :class="['subscription-row__chevron', { 'is-rotated': isExpanded }]"
        :icon="faChevronDown"
        view="table"
        :title="`Payments of subscription #${subscription.id}`"
        @click="toggleExpanded"
      />
    </AppTd>
    <AppTd
      v-if="withCustomer"
      class="zero-padding subscription-row__icon-cell"
    >
      <SubscriptionRowActions
        :subscription="subscription"
        :can-manage="canManage"
      />
    </AppTd>
  </AppTr>
</template>

<style scoped lang="scss" src="./SubscriptionRow.scss" />
